import IconButton from '@components/v2/ui/IconButton/IconButton';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Check, Plus } from 'lucide-react';
import { MouseEventHandler, memo } from 'react';
import LangEnum from '../../lang/LangEnum';
import { useAuthContext } from '../../modules/login/AuthContext';
import useOrderBook from '../../modules/order-book/hooks/useOrderBook';
import { useTranslation } from '../../modules/translation/TranslationContext';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import useSharedEntitystore from '../../stores/SharedEntities/sharedEntities.store';
import { CatalogProductsTestIds } from '../../utils/testIds/CatalogProducts.testIds';
import useAddToOrderButtonStyles from './styles/addToOrderButton.styles';
import { ShoppingProductButtonProps } from './types/shoppingProductButton.types';

const ShoppingProductButton = ({
  isShopping,
  isLoading,
  canAddShopping,
  shoppingButtonTooltipMessage,
  tabIndex = -1,
  onClickShopping,
}: ShoppingProductButtonProps) => {
  const handleChangeIsOpenLoginModal = useSharedEntitystore(
    (state) => state.handleChangeIsOpenLoginModal,
  );
  const hasSelectedOrder = useOrderBookStore((state) => Boolean(state.selectedOrder));
  const { openOrderBook } = useOrderBook();
  const { translate } = useTranslation();
  const { loggedin } = useAuthContext();
  const { classes } = useAddToOrderButtonStyles();

  const isAddBlocked = loggedin && !isShopping && !canAddShopping;

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (!loggedin) {
      handleChangeIsOpenLoginModal(true);
      return;
    }

    if (!hasSelectedOrder) {
      openOrderBook();
      return;
    }

    onClickShopping();
  };

  return (
    <Tooltip
      TooltipContent={
        isAddBlocked
          ? translate(
              shoppingButtonTooltipMessage || LangEnum.CATALOGS_PRODUCTS_UNAVAILABLE_PRODUCT,
            )
          : undefined
      }
    >
      <IconButton
        icon={isShopping ? Check : Plus}
        size="medium"
        shape="round"
        color={isShopping ? 'primary' : 'secondary'}
        className={isShopping ? classes.inOrder : undefined}
        disabled={isLoading || isAddBlocked}
        tabIndex={tabIndex}
        ariaLabel={translate(
          isShopping
            ? LangEnum.CATALOGS_PRODUCTS_REMOVE_FROM_ORDER
            : LangEnum.CATALOGS_PRODUCTS_ADD_TO_ORDER,
        )}
        testId={CatalogProductsTestIds.PRODUCT_ADD_BUTTON}
        onClick={handleClick}
      />
    </Tooltip>
  );
};

export default memo(ShoppingProductButton);
